export interface BackendError {
  error?: {
    message?: string;
    type?: string;
    param?: string | null;
    code?: string;
    timestamp?: string;
    details?: string[];
  };
}

export function extractBackendError(backendError: BackendError) {
  if (backendError?.error?.message) {
    return backendError.error.message;
  }

  if (backendError?.error?.details) {
    return backendError.error.details.join(', ');
  }

  return 'Your request could not be processed';
}

/**
 * API Error Response interface for Stripe and other payment errors
 * The backend now returns standardized error responses with message, detail, and code
 */
export interface ApiErrorResponse {
  message?: string;
  detail?: string;
  error?: string;
  code?: string;
}

/**
 * Extract a user-friendly error message from an API error response.
 * Handles various response formats including Stripe errors.
 *
 * @param error - The error object, typically from an axios catch block
 * @param fallbackMessage - The message to show if no specific error is found
 * @returns A user-friendly error message string
 */
export function getApiErrorMessage(
  error: any,
  fallbackMessage: string = 'An error occurred. Please try again.'
): string {
  // Handle axios error responses
  const data = error?.response?.data;

  if (data) {
    // Check for our standardized error response format (message, detail, error)
    if (typeof data.message === 'string' && data.message) {
      return data.message;
    }
    if (typeof data.detail === 'string' && data.detail) {
      return data.detail;
    }
    if (typeof data.error === 'string' && data.error) {
      return data.error;
    }

    // Handle nested error object
    if (data.error?.message) {
      return data.error.message;
    }

    // Handle array of errors
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors.map((e: any) => e.message || e).join('. ');
    }

    // Handle non_field_errors from Django
    if (
      Array.isArray(data.non_field_errors) &&
      data.non_field_errors.length > 0
    ) {
      return data.non_field_errors.join('. ');
    }
  }

  // Handle direct error message
  if (error?.message && typeof error.message === 'string') {
    // Filter out generic axios error messages
    if (
      !error.message.includes('Network Error') &&
      !error.message.includes('Request failed')
    ) {
      return error.message;
    }
  }

  return fallbackMessage;
}

/**
 * Extract error code from API response (useful for conditional error handling)
 */
export function getApiErrorCode(error: any): string | null {
  const data = error?.response?.data;
  if (data?.code) {
    return data.code;
  }
  return null;
}

/**
 * Error codes of the plan refusals (case_subscription/exceptions.py
 * default_code). CaseViewSet.create currently hardcodes "permission_denied"
 * in place of these, so the message patterns below are still needed; the
 * codes are matched first so a copy edit cannot drop the View plans action
 * once the backend emits them.
 */
const CASE_LIMIT_CODES = new Set([
  'case_limit_exceeded',
  'subscription_expired',
  'no_active_subscription',
]);

/**
 * True when the API refused to create something because of the user's plan:
 * the case limit for the billing period, an expired plan, or no active plan
 * at all. The backend sends these as 403 with error.type "permission_denied".
 */
export function isCaseLimitError(error: any): boolean {
  if (error?.response?.status !== 403) return false;
  const payload = error?.response?.data?.error;
  const code: string = payload?.code || '';
  if (CASE_LIMIT_CODES.has(code)) return true;
  const message: string = payload?.message || '';
  return (
    /cases this billing period/i.test(message) ||
    /case creation limit/i.test(message) ||
    /subscription is inactive/i.test(message) ||
    /subscription (has )?expired/i.test(message)
  );
}
